/**
 * Conversation Message Search Plugin
 * 
 * Sidebar search panel following Copilot design pattern.
 * Button is injected via Vite transform into SidepanelSwitch.vue
 * Uses API to search messages in current conversation.
 */

/* global axios */

import type { App } from 'vue';
import type { I18n } from 'vue-i18n';
import { nextTick } from 'vue';

// Import custom icon CSS
import '../assets/search-icon.css';

declare global {
  interface Window {
    CHATWOOT_PLUGINS: any;
    __chatwoot_store: any;
  }
}

let searchPanelCreated = false;
let vuexStore: any = null;
let i18nInstance: any = null;

interface InstallContext {
  i18n?: I18n;
  store?: any;
}

// Helper function to get translations
function t(key: string, fallback: string): string {
  if (i18nInstance?.global?.t) {
    const translated = i18nInstance.global.t(key);
    // If translation key is returned as-is, use fallback
    if (translated === key) return fallback;
    return translated;
  }
  return fallback;
}

export function install(app: App, { i18n, store }: InstallContext = {}) {
  vuexStore = store;
  i18nInstance = i18n;
  if (store) {
    window.CHATWOOT_PLUGINS = window.CHATWOOT_PLUGINS || {};
    window.CHATWOOT_PLUGINS.conversationMessageSearch = { store };
    window.__chatwoot_store = store;
  }

  // Initialize immediately, not in nextTick
  initializePlugin();
  // console.log('[MESSAGE-SEARCH] Plugin installed and initialized');
}

export default { install };

function initializePlugin() {
  // console.log('[MESSAGE-SEARCH] initializePlugin called');

  // Listen for toggle event from SidepanelSwitch.vue
  window.addEventListener('toggle-message-search', ((e: Event) => {
    // console.log('[MESSAGE-SEARCH] toggle-message-search event received', e);
    const event = e as CustomEvent;
    if (!searchPanelCreated) {
      createSearchPanel();
      searchPanelCreated = true;
    }

    const panel = document.getElementById('message-search-panel');
    // console.log('[MESSAGE-SEARCH] panel element:', panel);
    if (panel) {
      if (event.detail?.open) {
        // Slide in
        panel.classList.remove('translate-x-full', 'md:hidden');
        panel.classList.add('translate-x-0', 'md:flex');
        setTimeout(() => {
          const input = panel.querySelector('#search-input') as HTMLInputElement;
          if (input) input.focus();
        }, 100);
      } else {
        // Slide out
        panel.classList.add('translate-x-full', 'md:hidden');
        panel.classList.remove('translate-x-0', 'md:flex');
      }
    }
  }) as EventListener);

  // Keyboard shortcut
  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'F') {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('toggle-message-search', {
        detail: { open: true }
      }));
    }
    // ESC to close
    if (e.key === 'Escape') {
      const panel = document.getElementById('message-search-panel');
      if (panel && !panel.classList.contains('translate-x-full')) {
        closeSearchPanel();
      }
    }
  });
}

function closeSearchPanel() {
  const panel = document.getElementById('message-search-panel');
  if (panel) {
    panel.classList.add('translate-x-full', 'md:hidden');
    panel.classList.remove('translate-x-0', 'md:flex');
  }
  window.dispatchEvent(new CustomEvent('close-message-search'));
}

function createSearchPanel() {
  const mainContainer = document.querySelector('#app main');
  if (!mainContainer) {
    console.warn('[MESSAGE-SEARCH] Main container not found, retrying...');
    setTimeout(() => createSearchPanel(), 500);
    return;
  }

  if (document.getElementById('message-search-panel')) return;

  // Get translations
  const titleText = t('CONVERSATION_MESSAGE_SEARCH.TITLE', 'Buscar Mensagens');
  const placeholderText = t('CONVERSATION_MESSAGE_SEARCH.SEARCH_PLACEHOLDER', 'Buscar mensagens...');
  const emptyStateTitle = t('CONVERSATION_MESSAGE_SEARCH.EMPTY_STATE_TITLE', 'Buscar mensagens');
  const emptyStateDesc = t('CONVERSATION_MESSAGE_SEARCH.EMPTY_STATE_DESC', 'Digite para buscar mensagens nesta conversa');
  const searchingText = t('CONVERSATION_MESSAGE_SEARCH.SEARCHING', 'Buscando...');
  const noResultsTitle = t('CONVERSATION_MESSAGE_SEARCH.NO_RESULTS_TITLE', 'Nenhuma mensagem encontrada');
  const noResultsDesc = t('CONVERSATION_MESSAGE_SEARCH.NO_RESULTS_DESC', 'Tente um termo diferente');

  const panel = document.createElement('div');
  panel.id = 'message-search-panel';
  panel.className = 'bg-n-background h-full overflow-hidden flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none translate-x-full md:translate-x-0 md:hidden';

  panel.innerHTML = `
    <div class="flex flex-col h-full text-sm leading-6 tracking-tight w-full">
      <div class="flex items-center justify-between px-4 py-2 border-b border-n-weak h-12">
        <div class="flex items-center justify-between gap-2 flex-1">
          <span class="font-medium text-sm text-n-slate-12">${titleText}</span>
          <div class="flex items-center">
            <button id="search-close-btn" class="inline-flex items-center min-w-0 gap-2 transition-all duration-100 ease-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-blue-text hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-8 w-8 p-0 text-sm active:enabled:scale-[0.97] justify-center">
              <span class="i-lucide-x flex-shrink-0"></span>
            </button>
          </div>
        </div>
      </div>

      <div class="px-4 py-3 border-b border-n-weak">
        <div class="relative">
          <span class="i-lucide-search absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-9"></span>
          <input id="search-input" type="text" placeholder="${placeholderText}" class="w-full reset-base bg-n-alpha-3 pl-10 pr-10 py-2.5 text-sm border border-n-weak rounded-lg focus:outline-0 focus:outline-none focus:ring-2 focus:ring-n-blue-11 focus:border-n-blue-11 text-n-slate-12"/>
          <button id="search-clear-btn" class="absolute right-2 top-1/2 -translate-y-1/2 text-n-slate-9 hover:text-n-slate-11 hidden">
            <span class="i-lucide-x text-sm"></span>
          </button>
        </div>
      </div>

      <div id="search-results" class="flex-1 flex px-4 py-4 overflow-y-auto items-start">
        <div class="flex-1 flex flex-col gap-6 px-2">
          <div id="search-empty-state" class="flex flex-col space-y-4 py-4 items-center text-center">
            <span class="i-lucide-search text-n-slate-9 text-4xl"></span>
            <div class="space-y-1">
              <h3 class="text-base font-medium text-n-slate-12 leading-8">${emptyStateTitle}</h3>
              <p class="text-sm text-n-slate-11 leading-6">${emptyStateDesc}</p>
            </div>
          </div>
          <div id="search-loading" class="flex flex-col space-y-4 py-4 items-center text-center hidden">
            <span class="i-lucide-loader-2 text-n-slate-9 text-4xl animate-spin"></span>
            <div class="space-y-1">
              <h3 class="text-base font-medium text-n-slate-12 leading-8">${searchingText}</h3>
            </div>
          </div>
          <div id="search-results-list" class="w-full space-y-2 hidden"></div>
          <div id="search-no-results" class="flex flex-col space-y-4 py-4 items-center text-center hidden">
            <span class="i-lucide-search-x text-n-slate-9 text-4xl"></span>
            <div class="space-y-1">
              <h3 class="text-base font-medium text-n-slate-12 leading-8">${noResultsTitle}</h3>
              <p class="text-sm text-n-slate-11 leading-6">${noResultsDesc}</p>
            </div>
          </div>
        </div>
      </div>

      <div id="search-navigation" class="px-4 py-2 border-t border-n-weak hidden">
        <div class="flex items-center justify-between">
          <span id="search-count" class="text-xs text-n-slate-11"></span>
          <div class="flex items-center gap-1">
            <button id="search-prev-btn" class="inline-flex items-center justify-center h-8 w-8 rounded-lg text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50" disabled>
              <span class="i-lucide-chevron-up"></span>
            </button>
            <button id="search-next-btn" class="inline-flex items-center justify-center h-8 w-8 rounded-lg text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50" disabled>
              <span class="i-lucide-chevron-down"></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  `;

  // const ninjaKeys = mainContainer.querySelector('ninja-keys');
  // if (ninjaKeys) {
  //   ninjaKeys.insertAdjacentElement('afterend', panel);
  // } else {
  mainContainer.appendChild(panel);
  // }

  setupEventHandlers(panel);

  requestAnimationFrame(() => {
    panel.classList.remove('translate-x-full', 'md:hidden');
    panel.classList.add('translate-x-0', 'md:flex');
  });
}

function setupEventHandlers(panel: HTMLElement) {
  const closeBtn = panel.querySelector('#search-close-btn');
  const input = panel.querySelector('#search-input') as HTMLInputElement;
  const clearBtn = panel.querySelector('#search-clear-btn');

  closeBtn?.addEventListener('click', closeSearchPanel);

  let searchTimeout: number;
  input?.addEventListener('input', () => {
    const hasValue = input.value.length > 0;
    clearBtn?.classList.toggle('hidden', !hasValue);

    clearTimeout(searchTimeout);
    searchTimeout = window.setTimeout(() => performSearch(input.value, panel), 300);
  });

  clearBtn?.addEventListener('click', () => {
    input.value = '';
    clearBtn.classList.add('hidden');
    resetSearch(panel);
    input.focus();
  });
}

let searchResults: any[] = [];
let currentResultIndex = -1;

function resetSearch(panel: HTMLElement) {
  searchResults = [];
  currentResultIndex = -1;

  panel.querySelector('#search-empty-state')?.classList.remove('hidden');
  panel.querySelector('#search-results-list')?.classList.add('hidden');
  panel.querySelector('#search-no-results')?.classList.add('hidden');
  panel.querySelector('#search-navigation')?.classList.add('hidden');
  panel.querySelector('#search-loading')?.classList.add('hidden');
}

async function performSearch(query: string, panel: HTMLElement) {
  const emptyState = panel.querySelector('#search-empty-state');
  const resultsList = panel.querySelector('#search-results-list');
  const noResults = panel.querySelector('#search-no-results');
  const navigation = panel.querySelector('#search-navigation');
  const loading = panel.querySelector('#search-loading');

  if (!query || query.length < 2) {
    resetSearch(panel);
    return;
  }

  emptyState?.classList.add('hidden');
  noResults?.classList.add('hidden');
  resultsList?.classList.add('hidden');
  navigation?.classList.add('hidden');
  loading?.classList.remove('hidden');

  try {
    const messages = await searchMessagesAPI(query);

    loading?.classList.add('hidden');
    searchResults = messages;
    currentResultIndex = messages.length > 0 ? 0 : -1;

    if (messages.length === 0) {
      resultsList?.classList.add('hidden');
      noResults?.classList.remove('hidden');
      navigation?.classList.add('hidden');
      return;
    }

    noResults?.classList.add('hidden');
    resultsList?.classList.remove('hidden');
    navigation?.classList.remove('hidden');

    renderResults(messages, query, panel);

    if (currentResultIndex >= 0) {
      scrollToMessage(searchResults[currentResultIndex].id);
    }
  } catch (error) {
    console.error('[MESSAGE-SEARCH] API error:', error);
    loading?.classList.add('hidden');
    searchResults = [];
    currentResultIndex = -1;
    resultsList?.classList.add('hidden');
    navigation?.classList.add('hidden');
    noResults?.classList.remove('hidden');
  }
}

async function searchMessagesAPI(query: string): Promise<any[]> {
  const store = vuexStore || window.__chatwoot_store;
  if (!store) return [];

  const currentChat = store.getters['getSelectedChat'];
  if (!currentChat) return [];

  const accountId = store.getters['getCurrentAccountId'];
  const conversationId = currentChat.id;
  const axios = (window as any).axios;

  if (!axios) {
    console.error('[MESSAGE-SEARCH] window.axios not available');
    return [];
  }

  const perPage = 100;
  let page = 1;
  let totalPages = 1;
  const allMessages: any[] = [];

  do {
    const response = await axios.get(
      `/api/v1/accounts/${accountId}/conversations/${conversationId}/messages/search`,
      {
        params: { q: query, page, per_page: perPage },
      }
    );

    const payload = response.data.payload || {};
    const pageMessages = payload.messages || [];
    const meta = payload.meta || {};

    allMessages.push(...pageMessages);
    totalPages = Number(meta.total_pages || 1);
    page += 1;
  } while (page <= totalPages);

  return allMessages;
}

function formatMessageTime(msg: any): string {
  const labels: string[] = [];

  const createdAt = msg.created_at;
  const receivedAt = msg.received_at;
  const updatedAt = msg.updated_at;

  if (receivedAt) {
    labels.push(`Recebido em: ${new Date(receivedAt * 1000).toLocaleString()}`);
  }

  if (createdAt) {
    labels.push(`Criado em: ${new Date(createdAt * 1000).toLocaleString()}`);
  }

  if (updatedAt && updatedAt !== createdAt) {
    labels.push(`Atualizado em: ${new Date(updatedAt * 1000).toLocaleString()}`);
  }

  return labels.join('<br>');
}

function renderResults(results: any[], query: string, panel: HTMLElement) {
  const resultsList = panel.querySelector('#search-results-list');
  const countEl = panel.querySelector('#search-count');
  if (!resultsList) return;

  // Update count
  if (countEl) {
    countEl.textContent = `${results.length} result${results.length !== 1 ? 's' : ''} found`;
  }

  const html = results.map((msg: any, index: number) => {
    const highlighted = highlightText(msg.content.substring(0, 100), query);
    const isActive = index === currentResultIndex;
    const timeLabel = formatMessageTime(msg);
    const senderName = msg.sender?.name || msg.sender || 'Unknown';

    return `
      <button class="w-full px-3 py-2 rounded-md border ${isActive ? 'border-n-blue-6 bg-n-blue-2' : 'border-n-weak bg-n-slate-2'} text-left hover:bg-n-slate-3 transition-colors" data-index="${index}" data-message-id="${msg.id}">
        <div class="flex items-center gap-1 text-xs text-n-slate-10 mb-1">
          <span>${senderName}</span>
        </div>
        <div class="text-sm text-n-slate-11 line-clamp-2">${highlighted}${msg.content.length > 100 ? '...' : ''}</div>
        ${timeLabel ? `<div class="text-xs text-n-slate-9 mt-1">${timeLabel}</div>` : ''}
      </button>
    `;
  }).join('');

  resultsList.innerHTML = html;

  resultsList.querySelectorAll('button').forEach(btn => {
    btn.addEventListener('click', () => {
      const index = parseInt(btn.getAttribute('data-index') || '0');
      currentResultIndex = index;
      renderResults(searchResults, query, panel); // Re-render to update active state
      scrollToMessage(searchResults[index].id);
    });
  });
}

function highlightText(text: string, query: string): string {
  if (!query) return escapeHtml(text);

  let escaped = escapeHtml(text);

  // Stopwords comuns em português que devem ser ignoradas
  const stopwords = ['a', 'o', 'as', 'os', 'de', 'da', 'do', 'das', 'dos', 'em', 'no', 'na', 'nos', 'nas', 'um', 'uma', 'uns', 'umas', 'para', 'por', 'com', 'sem', 'ao', 'aos', 'à', 'às', 'e', 'ou'];

  // Split query into words and highlight each one
  // Filter out words with less than 3 characters OR stopwords
  const words = query.trim().split(/\s+/).filter(w => {
    const lowerWord = w.toLowerCase();
    return w.length >= 3 && !stopwords.includes(lowerWord);
  });

  words.forEach(word => {
    const regex = new RegExp(`(${escapeRegex(word)})`, 'gi');
    escaped = escaped.replace(regex, '<span class="bg-n-amber-4 text-n-amber-11 px-0.5 rounded">$1</span>');
  });

  return escaped;
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

async function scrollToMessage(messageId: number | string) {
  await ensureMessageLoaded(messageId);
  setTimeout(() => scrollToMessageElement(messageId), 150);
}

async function ensureMessageLoaded(messageId: number | string) {
  const store = vuexStore || window.__chatwoot_store;
  const axios = (window as any).axios;
  if (!store || !axios) return;

  const currentChat = store.getters['getSelectedChat'];
  const allMessagesLoaded = store.getters['getAllMessagesLoaded'];
  if (!currentChat?.id || !currentChat.messages?.length) return;

  const conversationId = currentChat.id;
  const numericMessageId = Number(messageId);
  let guard = 0;

  while (
    !document.querySelector(`[data-message-id="${messageId}"]`) &&
    !store.getters['getAllMessagesLoaded'] &&
    guard < 50
  ) {
    const oldestLoadedId = currentChat.messages[0]?.id;
    if (!oldestLoadedId || numericMessageId >= Number(oldestLoadedId)) {
      break;
    }

    const response = await axios.get(
      `/api/v1/accounts/${store.getters['getCurrentAccountId']}/conversations/${conversationId}/messages`,
      {
        params: { before: oldestLoadedId },
      }
    );

    const payload = response.data?.payload || [];
    const meta = response.data?.meta;

    if (meta) {
      store.commit('conversationMetadata/SET_CONVERSATION_METADATA', {
        id: conversationId,
        data: meta,
      });
    }

    if (!payload.length) {
      store.commit('SET_ALL_MESSAGES_LOADED', conversationId);
      break;
    }

    store.commit('SET_PREVIOUS_CONVERSATIONS', {
      id: conversationId,
      data: payload,
    });

    guard += 1;
  }

  if (allMessagesLoaded) {
    return;
  }
}

function scrollToMessageElement(messageId: number | string) {
  // Find message element in DOM by data-message-id attribute
  const messageEl = document.querySelector(`[data-message-id="${messageId}"]`) as HTMLElement;
  if (messageEl) {
    messageEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    messageEl.style.transition = 'outline 0.3s, outline-offset 0.3s';
    messageEl.style.outline = '2px solid var(--n-amber-9, #f59e0b)';
    messageEl.style.outlineOffset = '2px';
    setTimeout(() => {
      messageEl.style.outline = '';
      messageEl.style.outlineOffset = '';
    }, 1500);
  } else {
    console.warn('[MESSAGE-SEARCH] Message element not found in DOM:', messageId);
  }
}
